/**
 * VerifiedValue — A wrapper around FinancialValue that adds provenance tracking
 * and verification status semantics per the Phase 1 verification layer spec.
 *
 * Design Law: docs/plans/2026-07-24-k3-black-orange-design-law.md
 * Component Contract: All colors use CSS var tokens (no raw hex literals).
 *
 * Provenance chips:
 *   'E'        → Engine / deterministic calculation
 *   'AI'       → AI-derived value (needs-human orange background)
 *   'doc'      → Document-sourced value
 *   'override' → Human override
 *
 * Status semantics:
 *   'fresh'      → Default rendering, no extra decoration
 *   'stale'      → Visual de-emphasis (reduced opacity)
 *   'unavailable'→ NOT-RUN sentinel (never renders 0 or passes null to FinancialValue)
 *   'contested'  → Border using var(--color-blocker-tint)
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { FinancialValue } from './FinancialValue';
import StateChip from './StateChip';

/** Provenance kind indicating the source of the value */
export type ProvenanceKind = 'E' | 'AI' | 'doc' | 'override';

/** Verification status indicating the confidence/state of the value */
export type VerifiedValueStatus = 'fresh' | 'stale' | 'unavailable' | 'contested';

interface VerifiedValueProps {
  /** Numeric value to display (null/undefined only when status='unavailable') */
  value: number | null | undefined;
  /** Optional currency code (passed to FinancialValue) */
  currency?: string;
  /** Source/provenance of the value */
  provenance: ProvenanceKind;
  /** Verification status affecting visual presentation */
  status: VerifiedValueStatus;
  /** Optional drill-down handler; when provided, wraps output in a button */
  onDrill?: () => void;
  /** Additional CSS classes for the outer container (layout — margin, width, border) */
  className?: string;
  /** Additional CSS classes for the rendered figure itself (color, font-weight) —
   * forwarded to FinancialValue. Kept distinct from `className` so a layout class
   * on the container can never leak onto the number, and vice versa. */
  valueClassName?: string;
  /** Size variant for the financial value */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

/** Maps provenance kind to display label */
// Chip-sized glyphs (eyes-on finding 2026-07-25): the full words 'doc'/'override' center-clip
// inside the fixed chip — 'override' displayed its middle letters, literally reading "ERR" on an
// attestation surface. Single-glyph labels per the original spec (✎ for override); the full
// meaning lives in the title tooltip.
const PROVENANCE_LABELS: Record<ProvenanceKind, string> = {
  E: 'E',
  AI: 'AI',
  doc: 'D',
  override: '✎',
};

const PROVENANCE_TITLES: Record<ProvenanceKind, string> = {
  E: 'Engine-computed (deterministic)',
  AI: 'AI-asserted — no engine source',
  doc: 'Document-sourced',
  override: 'Manual override',
};

/** Maps provenance kind to background color CSS variable */
const PROVENANCE_BG_VARS: Record<ProvenanceKind, string> = {
  E: 'var(--color-text-muted)',
  AI: 'var(--color-needs-human)',
  doc: 'var(--color-text-secondary)',
  // K3 P2 (confirmed on eyes-on): brand orange here collided with the AI needs-human orange —
  // two different meanings, one hue. Override uses the strong neutral instead.
  override: 'var(--color-text-primary)',
};

/** Maps provenance kind to text color CSS variable */
const PROVENANCE_TEXT_VARS: Record<ProvenanceKind, string> = {
  E: 'var(--color-bg-base)',
  AI: 'var(--color-bg-base)',
  doc: 'var(--color-bg-base)',
  override: 'var(--color-bg-base)',
};

/**
 * Renders a 16×16px inline provenance chip.
 * All colors use CSS var tokens per design law.
 */
function ProvenanceChip({ kind }: { kind: ProvenanceKind }) {
  return (
    <span
      data-testid="provenance-chip"
      className={cn(
        'inline-flex items-center justify-center',
        'rounded-sm text-[10px] font-semibold uppercase leading-none'
      )}
      style={{
        width: '16px',
        height: '16px',
        backgroundColor: PROVENANCE_BG_VARS[kind],
        color: PROVENANCE_TEXT_VARS[kind],
      }}
      title={PROVENANCE_TITLES[kind]}
    >
      {PROVENANCE_LABELS[kind]}
    </span>
  );
}

/**
 * NOT-RUN sentinel for unavailable status.
 * Renders an em-dash or 'N/A' text node — never renders FinancialValue.
 */
function NotRunSentinel() {
  // P0 (K3 review 2026-07-25): the em-dash here collided with FinancialValue's verified-zero
  // convention — "not run" and "$0" looked identical on an attestation surface. The dormant
  // NOT RUN chip (same treatment EngineAuditTable already uses for missing engines) is the
  // unambiguous form; the em-dash stays reserved for verified zero.
  return (
    <span data-testid="not-run-sentinel" title="Not run — no value available">
      <StateChip state="dormant" text="NOT RUN" mono />
    </span>
  );
}

/**
 * VerifiedValue component — wraps FinancialValue with provenance and status semantics.
 */
export function VerifiedValue({
  value,
  currency,
  provenance,
  status,
  onDrill,
  className,
  valueClassName,
  size = 'md',
}: VerifiedValueProps) {
  // Container classes based on status
  const containerClasses = cn(
    'inline-flex items-center gap-2',
    // Stale status: visual de-emphasis
    status === 'stale' && 'opacity-60',
    // Contested status: border using var(--color-blocker-tint)
    status === 'contested' && 'border-2 rounded px-2 py-1',
    className
  );

  // Contested border style using CSS var
  const containerStyle: React.CSSProperties =
    status === 'contested'
      ? { borderColor: 'var(--color-blocker-tint)' }
      : {};

  // Content: unavailable status renders NOT-RUN sentinel.
  // valueClassName (NOT className — that's the container's) reaches the figure
  // itself, so a caller styling the number (font-bold, an explicit text color on
  // a colored card) actually lands on the rendered FinancialValue, not the
  // invisible flex container around it.
  const content =
    status === 'unavailable' ? (
      <NotRunSentinel />
    ) : (
      <FinancialValue value={value} size={size} className={valueClassName} />
    );

  const inner = (
    <>
      {content}
      {status === 'stale' && (
        <span
          data-testid="stale-marker"
          aria-label="value is stale — engine has not re-run"
          className="font-mono"
          style={{
            fontSize: '10px',
            color: 'var(--color-text-muted)',
          }}
        >
          (stale)
        </span>
      )}
      <ProvenanceChip kind={provenance} />
    </>
  );

  // When onDrill is provided, wrap in a button for accessibility
  if (onDrill) {
    return (
      <button
        type="button"
        data-testid="verified-value-button"
        className={cn(
          containerClasses,
          'cursor-pointer hover:opacity-80 transition-opacity'
        )}
        style={containerStyle}
        onClick={onDrill}
        aria-label={`View details for verified value (${provenance})`}
      >
        {inner}
      </button>
    );
  }

  // No onDrill: render as span container
  return (
    <span
      data-testid="verified-value-container"
      className={containerClasses}
      style={containerStyle}
    >
      {inner}
    </span>
  );
}

export default VerifiedValue;
